import { Theme, alpha } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

/*
  One row of the Customers page search box. The API merges customers, contacts and
  accounts into a single list (CustomerSearchListResponseBo), so a row only carries
  the fields its own type has - the rest come back null.
*/
/* Company: a HubSpot company no customer is tied to. */
export type CustomerSearchItemType = 'Customer' | 'Company' | 'Contact' | 'Account';

export interface ICustomerSearchItem {
  /* "<itemType>-<itemId>": ids repeat across the three sources, this one does not. */
  id: string;
  itemId: number;
  itemType: CustomerSearchItemType;
  name: string;
  /* Chip text: "Customer", "Hubspot Company", "Contact", or "LRP Account" / "CME Account" / "Master Account". */
  typeLabel: string;
  /* Customers only - an inactive customer is still listed, with an Inactive chip. */
  inactive: boolean;
  /* Contacts only: attached to more than one customer, shown after the name. */
  multipleCustomers?: boolean;
  /* Accounts only. */
  accountShort?: string | null;
  /*
    The customer whose dialog a contact or account opens. A contact has one only when it is
    attached to exactly one customer; otherwise it opens its own Contact dialog. A master
    account takes the customer its sub-accounts are on. Null for an account with no customer.
  */
  customerId?: number | null;
}

/*
  Each type reads from both ends of its row: the name in the type's own color, and the
  chip in the same hue a shade lighter (that is what variant="soft" does - text in
  <color>.dark on a 16% tint of <color>.main). Customers are the default case and take
  the surrounding text color with a plain gray chip.
*/
export const ITEM_TYPE_STYLES: Record<
  CustomerSearchItemType,
  { chipColor: 'default' | 'info' | 'success'; textColor?: string }
> = {
  Customer: { chipColor: 'default' },
  // Purple. The theme's secondary.main is not (it was changed to a gray-green), so the text uses
  // secondary.dark, and LABEL_CHIP_SX tints the chip from it.
  Company: { chipColor: 'default', textColor: 'secondary.dark' },
  Contact: { chipColor: 'info', textColor: 'info.dark' },
  Account: { chipColor: 'success', textColor: 'success.dark' },
};

export const itemTypeStyle = (itemType: CustomerSearchItemType) =>
  ITEM_TYPE_STYLES[itemType] ?? ITEM_TYPE_STYLES.Customer;

/*
  Chips whose soft color can't come from the chip's own color prop, keyed by label. The three
  account chips share the green, so they are told apart by depth, lightest to darkest: LRP a
  lighter tint than the standard soft one, CME a deeper one and Master deeper still, both with
  darker text. The Hubspot Company chip is the soft tint of its purple text.
*/
type ChipShade = (theme: Theme) => SystemStyleObject<Theme>;

const LABEL_CHIP_SX: Record<string, ChipShade> = {
  'Hubspot Company': (theme) => ({
    bgcolor: alpha(theme.palette.secondary.dark, 0.12),
    color: theme.palette.secondary.dark,
  }),
  'LRP Account': (theme) => ({ bgcolor: alpha(theme.palette.success.main, 0.08) }),
  'CME Account': (theme) => ({
    bgcolor: alpha(theme.palette.success.main, 0.32),
    color: theme.palette.success.darker,
  }),
  'Master Account': (theme) => ({
    bgcolor: alpha(theme.palette.success.main, 0.56),
    color: theme.palette.success.darker,
  }),
};

/* The shade for a chip label, to add after the chip's own sx; no change for the other labels. */
export const typeChipSx = (typeLabel: string): ChipShade => LABEL_CHIP_SX[typeLabel] ?? (() => ({}));
